import { computed, inject } from '@angular/core';
import { signalStoreFeature, type, withComputed, withMethods } from '@ngrx/signals';
import { ConstructionProjectFeatureKey } from '@construction/construction.enums';
import { ConstructionProjectRole } from '@construction/construction.roles';
import {
  ConstructionProjectFeaturesType,
  ConstructionProjectLoadModel,
} from '@construction/models/construction-project.model';
import { PRIMARY_DATA_SOURCE } from '@construction/modules/project-edit/projects.enum';
import { PerspectiveSelection } from '@construction/modules/project-edit/projects.types';
import { EnabledFeature } from '@construction/shared/construction-features-list/construction-features-list/construction-features-list.component';
import { ConstructionProjectRoleService } from '@construction/services/construction-project-role.service';

/** Derives the data-source perspective from the digital-twin feature flag's per-role aerial/ground data availability. */
function getPerspectiveByRole(
  features: ConstructionProjectFeaturesType[],
  role = EnabledFeature.OwnerEnabled,
): PerspectiveSelection {
  const featurePerspective = features.find((feature) => feature.name === ConstructionProjectFeatureKey.DIGITAL_TWIN);

  if (!featurePerspective) {
    return { dualData: false, primarySource: PRIMARY_DATA_SOURCE.owner };
  }

  const dualData = featurePerspective.aerial_data![role] && featurePerspective.ground_data![role];

  if (dualData) {
    return {
      dualData: true,
      primarySource: role === EnabledFeature.OwnerEnabled ? PRIMARY_DATA_SOURCE.owner : PRIMARY_DATA_SOURCE.epc,
    };
  }

  return {
    dualData: false,
    primarySource: featurePerspective.aerial_data![role] ? PRIMARY_DATA_SOURCE.owner : PRIMARY_DATA_SOURCE.epc,
  };
}

/** Reads `project` from upstream state. Injects `ConstructionProjectRoleService`. Owns role predicates, available-roles list, and `preferredDataSourcePerspective`. */
export function withProjectRoles() {
  return signalStoreFeature(
    { state: type<{ project: ConstructionProjectLoadModel }>() },
    withMethods(function projectRoleMethods(store) {
      const roleService = inject(ConstructionProjectRoleService);

      function hasProjectRole(role: Parameters<ConstructionProjectRoleService['hasRole']>[1]): boolean {
        return roleService.hasRole(store.project(), role);
      }

      return {
        hasProjectRole,
        extractAvailableProjectRoles(): ConstructionProjectRole[] {
          return [
            {
              condition:
                hasProjectRole('EPCQualityManagerRemediate') || hasProjectRole('EPCQualityManagerCreateRemediate'),
              role: ConstructionProjectRole.EpcQualityManagers,
            },
            {
              condition: hasProjectRole('OwnerConstructionManager'),
              role: ConstructionProjectRole.OwnerConstructionManager,
            },
            { condition: hasProjectRole('SupplierCompanyUser'), role: ConstructionProjectRole.UserFromSupplierCompany },
            { condition: hasProjectRole('OwnerCompanyUser'), role: ConstructionProjectRole.UserFromOwnerCompany },
            { condition: hasProjectRole('EPCCompanyUser'), role: ConstructionProjectRole.UserFromEpcCompany },
          ]
            .filter(({ condition }) => condition)
            .map(({ role }) => role);
        },
        getPreferredDataSourcePerspective(): PerspectiveSelection {
          const isEpc =
            hasProjectRole('EPCQualityManagerRemediate') || hasProjectRole('EPCQualityManagerCreateRemediate');

          return getPerspectiveByRole(
            store.project().feature_flags,
            isEpc ? EnabledFeature.EpcEnabled : EnabledFeature.OwnerEnabled,
          );
        },
      };
    }),
    withComputed((store) => ({
      availableProjectRoles: computed(() => store.extractAvailableProjectRoles()),
      preferredDataSourcePerspective: computed(() => store.getPreferredDataSourcePerspective()),
    })),
  );
}
